import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowClockwise, EyeSlash, CircleNotch, CheckCircle, XCircle, Eye } from "@phosphor-icons/react";

const StatusPill = ({ ok, label }) => (
    <div className={`flex items-center gap-1 text-xs font-bold ${ok ? "text-emerald-700" : "text-amber-700"}`}>
        {ok ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
        {label}
    </div>
);

const PublishSwitch = ({ published, onToggle, testId }) => (
    <button
        data-testid={testId}
        onClick={onToggle}
        title={published ? "Published — click to unpublish" : "Hidden from students — click to publish"}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border-2 transition-colors ${
            published
                ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                : "bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200"
        }`}
    >
        {published ? <Eye size={12} weight="bold" /> : <EyeSlash size={12} weight="bold" />}
        {published ? "Published" : "Hidden"}
    </button>
);

const Section = ({ title, sub, ready, published, total, children, headerActions }) => (
    <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b-2 border-[#BFD3FF] flex flex-wrap items-center gap-3 justify-between">
            <div>
                <div className="font-heading font-bold">{title}</div>
                {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
            </div>
            <div className="flex items-center gap-3">
                {typeof published === "number" && (
                    <span className="text-[11px] font-bold text-slate-600">
                        <span className="text-emerald-700">{published}</span> / {total} published
                    </span>
                )}
                <StatusPill ok={ready} label={ready ? "ready" : "not generated"} />
                {headerActions}
            </div>
        </div>
        <div className="p-5">{children}</div>
    </div>
);

export default function AdminCurrentAffairs() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null); // 'mcqs' | 'articles' | 'editorials' | 'all' | null

    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/current-affairs/today"); setData(data); }
        catch (e) { toast.error("Failed to load"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const regen = async (kind) => {
        if (!window.confirm(`Regenerate today's ${kind}? This deletes the current cached copy and re-runs the LLM (may take 60-120s).`)) return;
        setBusy(kind);
        try {
            await api.post("/admin/current-affairs/regenerate", { kind });
            toast.success(`Regenerated ${kind}`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); }
        finally { setBusy(null); }
    };

    const togglePublish = async (kind, item_id, currentPublished) => {
        try {
            await api.post("/admin/current-affairs/toggle-publish", {
                kind, date: data.date, item_id, published: !currentPublished,
            });
            toast.success(currentPublished ? "Hidden from students" : "Published to students");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
    };

    const hardHide = async (kind, item_id) => {
        if (!window.confirm(`Permanently remove this ${kind} from today's set? Use "Unpublish" if you may want to restore it.`)) return;
        try {
            await api.post("/admin/current-affairs/hide-item", { kind, date: data.date, item_id });
            toast.success("Removed");
            load();
        } catch (e) { toast.error("Remove failed"); }
    };

    if (!data) return <div className="text-slate-500 text-sm flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Loading…</div>;

    const mcqs = data.mcqs?.mcqs || [];
    const articles = data.articles?.articles || [];
    const editorials = data.editorials?.editorials || [];
    const countPub = (arr) => arr.filter((x) => x.published !== false).length;

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="text-slate-500">Date (IST): <span className="font-bold text-slate-800">{data.date}</span></div>
                <div className="text-[11px] text-slate-500 italic">Click <span className="font-semibold text-emerald-700">Published/Hidden</span> pill on any item to control what the student portal shows.</div>
                <button data-testid="ca-regen-all" onClick={() => regen("all")} disabled={busy === "all"} className="ml-auto btn-primary !py-2 !px-4 disabled:opacity-50">
                    {busy === "all" ? <CircleNotch size={14} className="animate-spin" /> : <ArrowClockwise size={14} weight="bold" />} Regenerate all
                </button>
            </div>

            <Section
                title="Daily MCQs"
                sub="Auto-included in the student's Daily CA test"
                ready={!!data.mcqs && data.mcqs.status === "ready"}
                published={countPub(mcqs)} total={mcqs.length}
                headerActions={
                    <button data-testid="ca-regen-mcqs" onClick={() => regen("mcqs")} disabled={!!busy} className="btn-ghost !py-1 !px-2 text-[10px] font-bold uppercase tracking-wider">
                        {busy === "mcqs" ? <CircleNotch size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />} Regen
                    </button>
                }
            >
                {mcqs.length === 0 ? <div className="text-slate-400 text-sm">No MCQs.</div> : (
                    <ol className="space-y-2 list-decimal list-inside text-sm">
                        {mcqs.map((m) => (
                            <li key={m.mcq_id} className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
                                <span className={`flex-1 ${m.published === false ? "text-slate-400 line-through" : "text-slate-700"}`}>{m.question}</span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <PublishSwitch published={m.published !== false} onToggle={() => togglePublish("mcq", m.mcq_id, m.published !== false)} testId={`ca-mcq-pub-${m.mcq_id}`} />
                                    <button onClick={() => hardHide("mcq", m.mcq_id)} className="text-slate-400 hover:text-red-600 text-xs p-1" title="Remove permanently"><EyeSlash size={13} /></button>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </Section>

            <Section
                title="Daily Articles"
                sub="Shown on /current-affairs/articles"
                ready={!!data.articles && data.articles.status === "ready"}
                published={countPub(articles)} total={articles.length}
                headerActions={
                    <button data-testid="ca-regen-articles" onClick={() => regen("articles")} disabled={!!busy} className="btn-ghost !py-1 !px-2 text-[10px] font-bold uppercase tracking-wider">
                        {busy === "articles" ? <CircleNotch size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />} Regen
                    </button>
                }
            >
                {articles.length === 0 ? <div className="text-slate-400 text-sm">No articles.</div> : (
                    <ul className="space-y-2 text-sm">
                        {articles.map((a) => (
                            <li key={a.article_id} className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
                                <div className={`flex-1 ${a.published === false ? "opacity-50" : ""}`}>
                                    <span className="text-[10px] font-bold text-[#1E3A8A] mr-2">{a.gs_paper}</span>
                                    <span className={`${a.published === false ? "line-through" : ""} text-slate-700`}>{a.title}</span>
                                    <span className="ml-2 text-[10px] text-slate-400">{a.source_paper}</span>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <PublishSwitch published={a.published !== false} onToggle={() => togglePublish("article", a.article_id, a.published !== false)} testId={`ca-art-pub-${a.article_id}`} />
                                    <button onClick={() => hardHide("article", a.article_id)} className="text-slate-400 hover:text-red-600 text-xs p-1" title="Remove permanently"><EyeSlash size={13} /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            <Section
                title="Daily Editorials"
                sub="Shown on /current-affairs/editorials"
                ready={!!data.editorials && data.editorials.status === "ready"}
                published={countPub(editorials)} total={editorials.length}
                headerActions={
                    <button data-testid="ca-regen-editorials" onClick={() => regen("editorials")} disabled={!!busy} className="btn-ghost !py-1 !px-2 text-[10px] font-bold uppercase tracking-wider">
                        {busy === "editorials" ? <CircleNotch size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />} Regen
                    </button>
                }
            >
                {editorials.length === 0 ? <div className="text-slate-400 text-sm">No editorials.</div> : (
                    <ul className="space-y-2 text-sm">
                        {editorials.map((e) => (
                            <li key={e.editorial_id || e.title} className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
                                <div className={`flex-1 ${e.published === false ? "opacity-50" : ""}`}>
                                    <span className={`${e.published === false ? "line-through" : ""} text-slate-700`}>{e.title}</span>
                                    <span className="ml-2 text-[10px] text-slate-400">{e.source}</span>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <PublishSwitch
                                        published={e.published !== false}
                                        onToggle={() => togglePublish("editorial", e.editorial_id, e.published !== false)}
                                        testId={`ca-ed-pub-${e.editorial_id || "legacy"}`}
                                    />
                                    <button onClick={() => hardHide("editorial", e.editorial_id || e.title)} className="text-slate-400 hover:text-red-600 text-xs p-1" title="Remove permanently"><EyeSlash size={13} /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Section>
        </div>
    );
}
